import { useMemo, useCallback } from 'react';
import { useAuthStore } from '@/store/authStore';
import type { IngestionFilter, DateRangeInput } from '@/lib/api/system/ingestion-observability';

/** Max 30 days — Cube.js handles aggregation for overview; BQ direct limited to 1-day search. */
const MAX_RANGE_DAYS = 30;

/** Default log window size in minutes for BQ direct queries. */
const LOG_WINDOW_MINUTES = 5;

function getIngestionDefaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setHours(start.getHours() - 1); // Default to last 1 hour
  return { start, end };
}

export interface IngestionStateParams {
  projectId: string | null;
  videoProcessorConfigId: string | null;
  assetId: string | null;
  stage: string | null;
  outcome: string | null;
  type: string | null;
  correlationId: string | null;
  dateStart: string | null;
  dateEnd: string | null;
  tab: string;
  pipelineView: string;
  explorerPage: number;
  explorerPageSize: number;
  logWindowStart: string | null;
  logWindowEnd: string | null;
  liveTail: boolean;
  cameraName: string | null;
}

export interface IngestionStateAdapter {
  get: () => IngestionStateParams;
  set: (updates: Partial<IngestionStateParams>) => void;
}

export function useIngestionState(
  adapter: IngestionStateAdapter,
  lockedFilters?: Partial<IngestionFilter>
) {
  const tenant = useAuthStore((state) => state.tenant);
  const tenantId = tenant?.tenantId ?? '';

  const params = adapter.get();
  const setParams = adapter.set;

  const filters: IngestionFilter = useMemo(
    () => ({
      ...{
        projectId: params.projectId ?? undefined,
        videoProcessorConfigId: params.videoProcessorConfigId ?? undefined,
        assetId: params.assetId ?? undefined,
        stage: params.stage ?? undefined,
        outcome: params.outcome ?? undefined,
        type: params.type ?? undefined,
      },
      ...lockedFilters,
    }),
    [params, lockedFilters]
  );

  const defaults = useMemo(() => getIngestionDefaultDateRange(), []);
  const dateRange: DateRangeInput = useMemo(
    () => ({
      dateStart: params.dateStart ?? defaults.start.toISOString(),
      dateEnd: params.dateEnd ?? defaults.end.toISOString(),
    }),
    [params.dateStart, params.dateEnd, defaults]
  );

  const dateRangeAsDate = useMemo(
    () => ({
      start: new Date(dateRange.dateStart),
      end: new Date(dateRange.dateEnd),
    }),
    [dateRange]
  );

  // --- Log Window ---
  const logWindow: DateRangeInput = useMemo(() => {
    if (params.logWindowStart && params.logWindowEnd) {
      return {
        dateStart: params.logWindowStart,
        dateEnd: params.logWindowEnd,
      };
    }
    return {
      dateStart: dateRange.dateStart,
      dateEnd: dateRange.dateEnd,
    };
  }, [params.logWindowStart, params.logWindowEnd, dateRange]);

  const canGoNewer = useMemo(() => {
    const windowEnd = new Date(logWindow.dateEnd).getTime();
    const rangeEnd = new Date(dateRange.dateEnd).getTime();
    return windowEnd < rangeEnd - 1000;
  }, [logWindow.dateEnd, dateRange.dateEnd]);

  const setLogWindow = useCallback(
    (start: Date, end: Date) => {
      setParams({
        logWindowStart: start.toISOString(),
        logWindowEnd: end.toISOString(),
        explorerPage: 1,
        liveTail: false,
      });
    },
    [setParams]
  );

  const shiftLogWindow = useCallback(
    (direction: 'older' | 'newer') => {
      const shiftMs = LOG_WINDOW_MINUTES * 60 * 1000;
      const currentStart = new Date(logWindow.dateStart);
      const currentEnd = new Date(logWindow.dateEnd);

      if (direction === 'older') {
        const newEnd = new Date(currentStart.getTime());
        const newStart = new Date(newEnd.getTime() - shiftMs);
        const rangeStart = new Date(dateRange.dateStart).getTime();
        if (newStart.getTime() < rangeStart) {
          setLogWindow(new Date(rangeStart), new Date(rangeStart + shiftMs));
        } else {
          setLogWindow(newStart, newEnd);
        }
      } else {
        const rangeEnd = new Date(dateRange.dateEnd).getTime();
        const newStart = new Date(currentEnd.getTime());
        const newEnd = new Date(Math.min(newStart.getTime() + shiftMs, rangeEnd));
        setLogWindow(newStart, newEnd);
      }
    },
    [logWindow, dateRange, setLogWindow]
  );

  const jumpToHistogramBucket = useCallback(
    (bucketTimestamp: string) => {
      const bucketTime = new Date(bucketTimestamp).getTime();
      const halfWindow = (LOG_WINDOW_MINUTES * 60 * 1000) / 2;
      const start = new Date(bucketTime - halfWindow);
      const end = new Date(bucketTime + halfWindow);
      setLogWindow(start, end);
    },
    [setLogWindow]
  );

  const resetLogWindow = useCallback(() => {
    const now = new Date();
    const start = new Date(now.getTime() - 60 * 60 * 1000); // last 1 hour
    setParams({
      dateStart: start.toISOString(),
      dateEnd: now.toISOString(),
      logWindowStart: null,
      logWindowEnd: null,
      explorerPage: 1,
    });
  }, [setParams]);

  // --- Date Range ---
  const setFilter = useCallback(
    (key: keyof IngestionFilter, value: string | null) => {
      setParams({ [key]: value });
    },
    [setParams]
  );

  const setDateRange = useCallback(
    (start: Date, end: Date) => {
      const ms = end.getTime() - start.getTime();
      const days = ms / (1000 * 60 * 60 * 24);
      if (days > MAX_RANGE_DAYS) {
        start = new Date(end.getTime() - MAX_RANGE_DAYS * 24 * 60 * 60 * 1000);
      }
      setParams({
        dateStart: start.toISOString(),
        dateEnd: end.toISOString(),
        logWindowStart: null,
        logWindowEnd: null,
        explorerPage: 1,
      });
    },
    [setParams]
  );

  const jumpAroundTime = useCallback(
    (time: Date, rangeMinutes = 60) => {
      const now = new Date();
      const halfRange = (rangeMinutes * 60 * 1000) / 2;
      const rangeStart = new Date(time.getTime() - halfRange);
      const rangeEnd = new Date(Math.min(time.getTime() + halfRange, now.getTime()));
      const halfWindow = (LOG_WINDOW_MINUTES * 60 * 1000) / 2;
      const windowStart = new Date(time.getTime() - halfWindow);
      const windowEnd = new Date(Math.min(time.getTime() + halfWindow, now.getTime()));
      setParams({
        dateStart: rangeStart.toISOString(),
        dateEnd: rangeEnd.toISOString(),
        logWindowStart: windowStart.toISOString(),
        logWindowEnd: windowEnd.toISOString(),
        explorerPage: 1,
        liveTail: false,
      });
    },
    [setParams]
  );

  const extendDateRange = useCallback(() => {
    const start = new Date(dateRange.dateStart);
    const end = new Date(dateRange.dateEnd);
    const currentMs = end.getTime() - start.getTime();
    const maxMs = MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;
    const newDuration = Math.min(currentMs * 2, maxMs);
    const newStart = new Date(end.getTime() - newDuration);
    setParams({
      dateStart: newStart.toISOString(),
      logWindowStart: null,
      logWindowEnd: null,
      explorerPage: 1,
    });
  }, [dateRange, setParams]);

  const clearFilters = useCallback(() => {
    setParams({
      projectId: null,
      videoProcessorConfigId: null,
      assetId: null,
      stage: null,
      outcome: null,
      type: null,
      correlationId: null,
    });
  }, [setParams]);

  const setLiveTail = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        setParams({
          liveTail: true,
          logWindowStart: null,
          logWindowEnd: null,
          explorerPage: 1,
        });
      } else {
        setParams({ liveTail: false });
      }
    },
    [setParams]
  );

  return {
    tenantId,
    filters,
    dateRange,
    dateRangeAsDate,
    setFilter,
    setDateRange,
    clearFilters,
    params,
    setParams,
    logWindow,
    canGoNewer,
    setLogWindow,
    shiftLogWindow,
    jumpToHistogramBucket,
    resetLogWindow,
    liveTail: params.liveTail,
    setLiveTail,
    jumpAroundTime,
    extendDateRange,
  };
}
